/**
 * ExternalSystemsPage (P3-C Doc 38) — Admin manage external integrations.
 */
import { Plus, Search } from "lucide-react";
import * as React from "react";

import {
  type ExternalSystem,
  externalSystemsApi,
} from "@/api/externalSystems";
import { ExternalSystemCard } from "@/components/admin/ExternalSystemCard";
import { ExternalSystemDialog } from "@/components/admin/ExternalSystemDialog";
import { GeneratedDocViewer } from "@/components/admin/GeneratedDocViewer";
import { EmptyData } from "@/components/illustrations/EmptyData";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { LoadingCards } from "@/components/ui/loading-cards";
import { useUIStore } from "@/stores/ui";

export function ExternalSystemsPage(): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);

  const [items, setItems] = React.useState<ExternalSystem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState("");
  const [busyMap, setBusyMap] = React.useState<Set<string>>(new Set());

  // Dialog state
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingSystem, setEditingSystem] = React.useState<ExternalSystem | null>(null);

  // Doc viewer
  const [viewingId, setViewingId] = React.useState<string | null>(null);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await externalSystemsApi.list();
      setItems(r.items);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Không tải được list",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  }, [pushToast]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const setBusy = (id: string, busy: boolean): void => {
    setBusyMap((s) => {
      const next = new Set(s);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleSync = async (sys: ExternalSystem): Promise<void> => {
    setBusy(sys.id, true);
    try {
      const res = await externalSystemsApi.sync(sys.id);
      if (res.sync_status === "done") {
        pushToast({
          variant: "success",
          title: `Đã sync ${sys.name}`,
          description: `${(res.doc_metadata.char_count as number | undefined) ?? 0} chars generated`,
        });
      } else {
        pushToast({
          variant: "destructive",
          title: "Sync lỗi",
          description: res.last_error ?? "Unknown error",
        });
      }
      await refresh();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Sync thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(sys.id, false);
    }
  };

  const handleDelete = async (sys: ExternalSystem): Promise<void> => {
    if (!confirm(`Xoá system "${sys.name}"? (Cũng xoá generated doc)`)) {
      return;
    }
    setBusy(sys.id, true);
    try {
      await externalSystemsApi.delete(sys.id);
      pushToast({ variant: "success", title: "Đã xoá" });
      await refresh();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Xoá thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(sys.id, false);
    }
  };

  const filtered = React.useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.slug.toLowerCase().includes(q) ||
        (s.description ?? "").toLowerCase().includes(q),
    );
  }, [items, filter]);

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4">
      {/* Header */}
      <header className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-xl font-semibold">External Systems</h1>
          <p className="text-sm text-muted-foreground">
            Connect git / database / URL → AI auto-gen documentation. Agent
            đọc lại doc khi user hỏi về hệ thống.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Tìm system..."
              className="pl-8 h-8 text-sm w-[200px]"
            />
          </div>
          <Button
            variant="brand"
            size="sm"
            onClick={() => {
              setEditingSystem(null);
              setDialogOpen(true);
            }}
          >
            <Plus className="size-4 mr-1" /> Thêm system
          </Button>
        </div>
      </header>

      {/* Body */}
      {loading ? (
        <LoadingCards count={3} />
      ) : items.length === 0 ? (
        <EmptyState
          hero={<EmptyData size={120} />}
          title="Chưa có external system nào"
          description="Connect git repo, database, hoặc URL để AI auto-gen documentation."
          action={
            <Button
              variant="brand"
              onClick={() => {
                setEditingSystem(null);
                setDialogOpen(true);
              }}
            >
              <Plus className="size-4 mr-1" /> Thêm system đầu tiên
            </Button>
          }
        />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((sys) => (
              <ExternalSystemCard
                key={sys.id}
                system={sys}
                busy={busyMap.has(sys.id)}
                onSync={() => void handleSync(sys)}
                onViewDoc={() => setViewingId(sys.id)}
                onEdit={() => {
                  setEditingSystem(sys);
                  setDialogOpen(true);
                }}
                onDelete={() => void handleDelete(sys)}
              />
            ))}
          </div>
          {filtered.length === 0 && filter ? (
            <div className="text-center text-sm text-muted-foreground py-8">
              Không có system nào match "{filter}"
            </div>
          ) : null}
        </>
      )}

      <ExternalSystemDialog
        open={dialogOpen}
        initial={editingSystem}
        onClose={() => {
          setDialogOpen(false);
          setEditingSystem(null);
        }}
        onSaved={refresh}
      />

      <GeneratedDocViewer
        systemId={viewingId}
        onClose={() => setViewingId(null)}
      />
    </div>
  );
}
